import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, ArrowLeft, Check, Loader2, Map, Wrench, RefreshCw,
  Search, Zap, ShoppingCart, Heart, ShieldCheck, Mail, RotateCcw, HelpCircle,
} from "lucide-react";

const ORG_TYPES = [
  { key: "business", label: "Small business", industry: "Small Business" },
  { key: "contractor", label: "Contractor / trades", industry: "Construction" },
  { key: "church", label: "Church / ministry", industry: "Church & Ministry" },
  { key: "nonprofit", label: "Nonprofit", industry: "Nonprofit" },
  { key: "restaurant", label: "Restaurant / food", industry: "Restaurants" },
  { key: "coach", label: "Coach / consultant", industry: "Coaching" },
  { key: "creator", label: "Creator / personal brand", industry: "Personal Brands" },
  { key: "other", label: "Something else", industry: "" },
];

const GOALS = [
  { key: "leads", label: "Get more leads", icon: Search },
  { key: "professional", label: "Look more professional", icon: ShieldCheck },
  { key: "sell", label: "Sell online", icon: ShoppingCart },
  { key: "donations", label: "Grow donations", icon: Heart },
  { key: "time", label: "Save time on busywork", icon: Zap },
  { key: "other", label: "Something else", icon: HelpCircle },
];

const STAGES = [
  { key: "idea", label: "Just an idea", line: "You're starting fresh — the goal is a clean, credible launch, fast." },
  { key: "broken", label: "Have a site, it's not working", line: "You have a presence but it isn't converting — we'll fix what's leaking." },
  { key: "grow", label: "Established, want to grow", line: "You've got traction — now it's about compounding growth." },
  { key: "other", label: "Something else / not sure", line: "Every business is different — here's a solid starting point we'll tailor to you." },
];

// Rule-based teaser: assemble 3 prioritized moves from the answers. Honest "preview".
function buildRoadmap({ org, goal, orgOther }) {
  const orgLabel = (org === "other" ? (orgOther || "business") : (ORG_TYPES.find((o) => o.key === org)?.label || "business")).toLowerCase();
  const M1 = {
    leads: { icon: Search, text: `A conversion-focused ${orgLabel} website with lead capture built in` },
    professional: { icon: ShieldCheck, text: `A premium ${orgLabel} design that earns trust on the first click` },
    sell: { icon: ShoppingCart, text: `An online storefront with cart and secure checkout` },
    donations: { icon: Heart, text: `A giving-first layout with recurring donations and impact storytelling` },
    time: { icon: Zap, text: `AI-assisted content and automated intake so you stop doing busywork` },
  };
  const M2 = {
    leads: { icon: Map, text: "Local SEO so you show up when people search for you" },
    professional: { icon: Search, text: "SEO + Google Business Profile so the right people find you" },
    sell: { icon: Zap, text: "Product pages and email follow-ups tuned to convert browsers to buyers" },
    donations: { icon: RefreshCw, text: "Recurring giving + donor email journeys that keep support flowing" },
    time: { icon: Wrench, text: "AI lead intake that drafts your replies (you just approve & send)" },
  };
  const move1 = M1[goal] || { icon: ShieldCheck, text: `A custom ${orgLabel} website designed around your specific goals` };
  const move2 = M2[goal] || { icon: Search, text: "SEO + Google Business Profile so the right people find you" };
  const move3 = { icon: RefreshCw, text: "Hosting & Care to keep it secure, current and growing after launch" };
  return [move1, move2, move3];
}

const optCls = (active) =>
  `w-full text-left px-4 py-3.5 border transition-all flex items-center gap-3 ${
    active ? "border-gold bg-gold/10 text-white" : "border-white/12 text-gray-300 hover:border-gold/50 hover:bg-white/[0.03]"
  }`;

function OtherBox({ stepKey, placeholder, value, onChange, onSubmit }) {
  return (
    <div className="mt-3 flex flex-col sm:flex-row gap-2" data-testid={`tf-other-${stepKey}`}>
      <input autoFocus value={value} onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSubmit()} placeholder={placeholder}
        data-testid={`tf-other-input-${stepKey}`}
        className="flex-1 bg-white/5 border border-gold/40 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none placeholder:text-gray-600" />
      <button onClick={onSubmit} data-testid={`tf-other-continue-${stepKey}`}
        className="bg-gold text-navy px-5 py-3 font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-1.5">
        Continue <ArrowRight size={15} />
      </button>
    </div>
  );
}

export function TopFosterPreview() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0-2 questions, 3 generating, 4 result
  const [a, setA] = useState({ org: "", goal: "", stage: "", orgOther: "", goalOther: "", stageOther: "" });
  const [otherOpen, setOtherOpen] = useState(false); // "other" text box visible for current step
  const [otherText, setOtherText] = useState("");
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailed, setEmailed] = useState(false);

  const advance = (key, val, extra = "") => {
    const next = { ...a, [key]: val, [`${key}Other`]: extra };
    setA(next);
    setOtherOpen(false); setOtherText("");
    if (key === "org") setStep(1);
    else if (key === "goal") setStep(2);
    else { setStep(3); setTimeout(() => setStep(4), 1600); }
  };

  const choose = (key, val) => {
    if (val === "other") { setOtherOpen(true); setOtherText(""); return; }
    advance(key, val);
  };

  const submitOther = (key) => {
    if (!otherText.trim()) { toast.error("Please tell us a bit more so Top Foster can tailor this."); return; }
    advance(key, "other", otherText.trim());
  };

  const back = (toStep) => { setStep(toStep); setOtherOpen(false); setOtherText(""); };
  const industry = a.org === "other" ? a.orgOther : (ORG_TYPES.find((o) => o.key === a.org)?.industry || "");
  const goalLabel = a.goal === "other" ? a.goalOther : (GOALS.find((g) => g.key === a.goal)?.label || "");
  const stageLabel = a.stage === "other" ? a.stageOther : (STAGES.find((s) => s.key === a.stage)?.label || "");
  const stageLine = STAGES.find((s) => s.key === a.stage)?.line || "";
  const roadmap = a.goal ? buildRoadmap(a) : [];

  const goFull = () => {
    const q = new URLSearchParams();
    if (industry) q.set("industry", industry);
    q.set("from_preview", "1");
    navigate(`/start?${q.toString()}`);
  };

  const restart = () => {
    setStep(0); setA({ org: "", goal: "", stage: "", orgOther: "", goalOther: "", stageOther: "" });
    setEmailMode(false); setSent(false); setEmail(""); setOtherOpen(false); setOtherText("");
  };

  const emailRoadmap = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error("Please enter a valid email."); return; }
    setSending(true);
    try {
      const res = await api.post("/inquiries", {
        name: email.split("@")[0], email: email.trim(), industry,
        goals: `Home preview — goal: ${goalLabel}; stage: ${stageLabel || a.stage}`,
        action: "save", cta_source: "home-preview",
        roadmap: roadmap.map((m) => m.text), goal_label: goalLabel, email_roadmap: true,
      });
      setEmailed(!!res.data?.emailed);
      setSent(true);
      toast.success(res.data?.emailed ? "Roadmap sent — check your inbox!" : "Saved — we'll be in touch!");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSending(false); }
  };

  return (
    <section className="py-24 md:py-28 max-w-5xl mx-auto px-6 md:px-12" data-testid="tf-preview">
      <div className="text-center mb-10">
        <div className="flex items-center justify-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4">
          <Sparkles size={15} /> See Top Foster in action
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">Get a taste of your roadmap in 60 seconds</h2>
        <p className="text-gray-400 mt-4 max-w-xl mx-auto">Answer 3 quick questions — no signup — and Top Foster will sketch your first three moves.</p>
      </div>

      <div className="relative glass border border-gold/25 p-6 md:p-10 min-h-[380px]">
        {step < 4 && (
          <div className="flex items-center gap-2 mb-8" data-testid="tf-progress">
            {[0, 1, 2].map((i) => (<div key={i} className={`h-1 flex-1 rounded ${step >= i ? "bg-gold" : "bg-white/10"}`} />))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="q0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <h3 className="font-heading text-xl text-white mb-6">What kind of organization are you?</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {ORG_TYPES.map((o) => (
                  <button key={o.key} onClick={() => choose("org", o.key)} data-testid={`tf-org-${o.key}`}
                    className={optCls(o.key === "other" && otherOpen)}>
                    {o.key === "other" ? <HelpCircle size={16} className="text-gold shrink-0" /> : <span className="w-2 h-2 rounded-full bg-gold shrink-0" />} {o.label}
                  </button>
                ))}
              </div>
              {otherOpen && <OtherBox stepKey="org" placeholder="Tell us what kind of organization you are…" value={otherText} onChange={setOtherText} onSubmit={() => submitOther("org")} />}
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="q1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <button onClick={() => back(0)} className="text-gray-400 hover:text-gold text-sm flex items-center gap-1.5 mb-5"><ArrowLeft size={14} /> Back</button>
              <h3 className="font-heading text-xl text-white mb-6">What's your #1 goal right now?</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {GOALS.map((g) => (
                  <button key={g.key} onClick={() => choose("goal", g.key)} data-testid={`tf-goal-${g.key}`}
                    className={optCls(g.key === "other" && otherOpen)}>
                    <g.icon size={17} className="text-gold shrink-0" /> {g.label}
                  </button>
                ))}
              </div>
              {otherOpen && <OtherBox stepKey="goal" placeholder="What's your main goal right now?" value={otherText} onChange={setOtherText} onSubmit={() => submitOther("goal")} />}
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="q2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <button onClick={() => back(1)} className="text-gray-400 hover:text-gold text-sm flex items-center gap-1.5 mb-5"><ArrowLeft size={14} /> Back</button>
              <h3 className="font-heading text-xl text-white mb-6">Where are you today?</h3>
              <div className="space-y-3">
                {STAGES.map((s) => (
                  <button key={s.key} onClick={() => choose("stage", s.key)} data-testid={`tf-stage-${s.key}`}
                    className={optCls(s.key === "other" && otherOpen)}>
                    {s.key === "other" ? <HelpCircle size={16} className="text-gold shrink-0" /> : <span className="w-2 h-2 rounded-full bg-gold shrink-0" />} {s.label}
                  </button>
                ))}
              </div>
              {otherOpen && <OtherBox stepKey="stage" placeholder="Tell us where you're at…" value={otherText} onChange={setOtherText} onSubmit={() => submitOther("stage")} />}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="gen" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-16 text-center" data-testid="tf-generating">
              <Loader2 className="animate-spin text-gold mb-5" size={34} />
              <div className="text-white font-heading text-lg">Top Foster is mapping your roadmap…</div>
              <div className="text-gray-500 text-sm mt-2">Reading your answers and prioritizing your first moves.</div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="res" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="tf-result">
              <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-3"><Sparkles size={14} /> Your Top Foster preview</div>
              <p className="text-gray-200 text-lg font-light leading-relaxed mb-8">{stageLine} Here are the first three moves I'd prioritize to <span className="text-gold">{(goalLabel || "grow your business").toLowerCase()}</span>:</p>
              <div className="space-y-3">
                {roadmap.map((m, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12 }}
                    className="flex items-start gap-4 bg-white/[0.03] border border-white/10 p-4" data-testid={`tf-move-${i + 1}`}>
                    <span className="w-8 h-8 shrink-0 rounded-full bg-gold text-navy font-bold flex items-center justify-center">{i + 1}</span>
                    <m.icon size={18} className="text-gold shrink-0 mt-1.5" />
                    <span className="text-gray-200">{m.text}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-white/10 flex items-start gap-2.5">
                <Check size={16} className="text-gold shrink-0 mt-0.5" />
                <p className="text-xs text-gray-500 leading-relaxed">This is a quick preview. Your full roadmap goes deeper — Top Foster builds your Business Profile and tailors the plan, timeline and investment to your exact situation.</p>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button onClick={goFull} data-testid="tf-fullroadmap-cta" className="flex-1 bg-gold text-navy py-3.5 font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2">
                  Get my full roadmap <ArrowRight size={17} />
                </button>
                {!emailMode && !sent && (
                  <button onClick={() => setEmailMode(true)} data-testid="tf-email-toggle" className="flex-1 border border-white/20 text-white py-3.5 font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-2">
                    <Mail size={16} /> Email me this roadmap
                  </button>
                )}
              </div>

              {emailMode && !sent && (
                <div className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="tf-email-form">
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@business.com" data-testid="tf-email-input"
                    className="flex-1 bg-white/5 border border-white/15 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none placeholder:text-gray-600" />
                  <button onClick={emailRoadmap} disabled={sending} data-testid="tf-email-submit"
                    className="bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {sending ? <Loader2 className="animate-spin" size={16} /> : "Send it"}
                  </button>
                </div>
              )}
              {sent && (
                <p className="mt-4 text-sm text-emerald-300 flex items-center gap-2" data-testid="tf-email-sent">
                  <Check size={15} /> {emailed
                    ? "Sent — check your inbox! Ask Top Foster will follow up personally."
                    : "Saved! Ask Top Foster will email your roadmap and follow up personally, shortly."}
                </p>
              )}

              <button onClick={restart} data-testid="tf-restart" className="mt-6 text-gray-500 hover:text-gold text-sm flex items-center gap-1.5"><RotateCcw size={13} /> Start over</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
